import { createContext, createElement, useContext, useEffect, useMemo, useState } from "react";
import { PRODUCTS, postageFor } from "./catalog";
import { api } from "./api";

const CartContext = createContext(null);
const KEY = "pow_cart_v1";

const load = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "{}");
    return {
      items: v.items && typeof v.items === "object" ? v.items : {},
      state: v.state || "",
      postcode: v.postcode || "",
    };
  } catch {
    return { items: {}, state: "", postcode: "" };
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load().items);
  const [state, setState] = useState(() => load().state);
  const [postcode, setPostcode] = useState(() => load().postcode);
  const [live, setLive] = useState(null);
  const [open, setOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify({ items, state, postcode }));
  }, [items, state, postcode]);

  useEffect(() => {
    const count = Object.values(items).reduce((s, q) => s + q, 0);
    if (!/^\d{4}$/.test(postcode) || count < 1) {
      setLive(null);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/postage", { params: { postcode, qty: count } });
        setLive(data?.postage != null ? data : null);
      } catch {
        setLive(null);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [postcode, items]);

  const value = useMemo(() => {
    const lines = [];
    PRODUCTS.forEach((p) => {
      (p.colors || [null]).forEach((col) => {
        const key = col ? `${p.id}::${col}` : p.id;
        if (items[key] > 0) lines.push({ ...p, key, variant: col, qty: items[key], total: p.price * items[key] });
      });
    });
    const count = lines.reduce((s, l) => s + l.qty, 0);
    const subtotal = lines.reduce((s, l) => s + l.total, 0);
    const liveValid = live && String(live.qty) === String(count);
    const postage = liveValid ? live.postage : state ? postageFor(state, count) : 0;
    return {
      items, lines, count, subtotal, postage, live: liveValid ? live : null,
      total: subtotal + postage,
      state, setState, postcode, setPostcode, open, setOpen, checkout, setCheckout,
      add: (key, qty = 1) => setItems((c) => ({ ...c, [key]: Math.min(200, (c[key] || 0) + qty) })),
      setQty: (key, qty) => setItems((c) => ({ ...c, [key]: Math.max(0, Math.min(200, qty)) })),
      clear: () => setItems({}),
    };
  }, [items, state, postcode, live, open, checkout]);

  return createElement(CartContext.Provider, { value }, children);
}

export const useCart = () => useContext(CartContext);
